import { createRoot, hydrateRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { SSG_ROUTES, SSG_ROUTE_MODULES } from "./AppRoutes";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { initDataLayer, checkStoredConsent, initGTM } from "./lib/gtm";

initSentry();  // antes de qualquer createRoot
import { fetchProductByHandle } from "./lib/shopify";

// Build version stamp
console.log(`[NatHof] Build: ${new Date().toISOString()} | Env: ${import.meta.env.MODE}`);

// DEV-only: Microsoft Clarity diagnostic
if (import.meta.env.DEV) {
  setTimeout(() => {
    const clarityTag = document.querySelector('script[src*="clarity.ms/tag"]');
    const clarityFn = typeof (window as any).clarity === 'function';
    console.log('[NAT HOF][Clarity] script detected:', !!clarityTag);
    console.log('[NAT HOF][Clarity] projectId=ro1a942hcq');
    console.log('[NAT HOF][Clarity] window.clarity available=', clarityFn);
    console.log('[NAT HOF][Clarity] environment=', import.meta.env.MODE);
  }, 2000);
}

// Initialize analytics (respecting consent)
// NOTA: o `consent default` é declarado UMA ÚNICA VEZ no index.html (gtag), antes
// do GTM. pushConsentDefault() foi removido daqui para não criar um segundo default
// conflitante no dataLayer.
initDataLayer();
checkStoredConsent();
initGTM();

// Expose auditKits on window for production diagnostics
const KIT_HANDLES = [
  { sku: 'KIT01', handle: 'kit-regeneracao-e-hidratacao-intensiva' },
  { sku: 'KIT02', handle: 'kit-hidratacao-e-limpeza-suave' },
  { sku: 'KIT03', handle: 'kit-hidratacao-diaria-essencial' },
];

(window as any).auditKits = async () => {
  console.log('[AuditKits] Checking 3 kits...');
  for (const kit of KIT_HANDLES) {
    try {
      const product = await fetchProductByHandle(kit.handle);
      if (!product) {
        console.log(`❌ ${kit.sku} (${kit.handle}): NOT FOUND`);
        continue;
      }
      const hasTitle = !!product.title;
      const hasHandle = !!product.handle;
      const hasImage = product.images?.edges?.length > 0 || product.images?.nodes?.length > 0;
      const hasVariant = product.variants?.edges?.length > 0;
      const status = hasTitle && hasHandle && hasImage && hasVariant ? '✅' : '⚠️';
      console.log(`${status} ${kit.sku} (${kit.handle}):`, {
        title: hasTitle ? product.title : '❌ MISSING',
        handle: hasHandle ? product.handle : '❌ MISSING',
        images: hasImage ? '✅' : '❌ MISSING',
        variants: hasVariant ? `✅ (${product.variants.edges.length})` : '❌ MISSING',
      });
    } catch (err) {
      console.log(`❌ ${kit.sku} (${kit.handle}): ERROR`, err);
    }
  }
  console.log('[AuditKits] Done.');
};

// ── Mount ────────────────────────────────────────────────────────────────────
// hydrateRoot ONLY on SSG pilot routes whose #root holds real SSG content
// (not the h1+description fallback). Every other route: createRoot as before.
const rootEl = document.getElementById("root")!;
const ssgPath = window.location.pathname.toLowerCase().replace(/\/+$/, "") || "/";
const shouldHydrate =
  SSG_ROUTES.includes(ssgPath) &&
  rootEl.firstElementChild !== null &&
  !rootEl.querySelector('[data-ssg]');

const tree = (
  <HelmetProvider>
    <App />
  </HelmetProvider>
);

if (shouldHydrate) {
  (window as any).__NATHOF_HYDRATING__ = true;
  (window as any).__NATHOF_RECOVERABLE_ERRORS__ = 0;
  // Load the page chunk first so hydration does not wait on the lazy import.
  SSG_ROUTE_MODULES[ssgPath]()
    .catch(() => undefined)
    .then(() => {
      hydrateRoot(rootEl, tree, {
        onRecoverableError(error) {
          (window as any).__NATHOF_RECOVERABLE_ERRORS__++;
          Sentry.captureException(error, { tags: { ssg_hydration: ssgPath } });
        },
      });
    });
} else {
  createRoot(rootEl).render(tree);
}
